<template>
  <div
    v-click-outside="closeDropdown"
    class="v2-dropdown"
  >
    <div
      class="v2-dropdown__trigger"
      @click="isOpen = !isOpen"
    >
      <slot
        name="toggle"
        :is-open="isOpen"
      >
        <button
          class="v2-dropdown__toggle"
          :class="{ 'v2-dropdown__toggle--has-value': !!value }"
        >
          <span class="v2-dropdown__label">{{ displayLabel }}</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="v2-dropdown__chevron"
            :class="{ 'v2-dropdown__chevron--open': isOpen }"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </slot>
    </div>
    <div
      v-if="isOpen"
      class="v2-dropdown__menu"
      :class="{ 'v2-dropdown__menu--right': align === 'right' }"
    >
      <slot
        name="menu"
        :close="closeDropdown"
      >
        <div
          v-if="searchable"
          class="v2-dropdown__search"
        >
          <input
            ref="searchInput"
            v-model="searchText"
            class="v2-dropdown__search-input"
            placeholder="Search"
            @input="$emit('search', searchText)"
            @keydown.enter.prevent="onEnterCreate"
          >
        </div>
        <div
          v-if="!filteredOptions.length && !canCreate"
          class="v2-dropdown__empty"
        >
          {{ emptyText }}
        </div>
        <div
          v-else
          class="v2-dropdown__options"
        >
          <button
            v-for="option in filteredOptions"
            :key="optionValue(option)"
            class="v2-dropdown__option"
            :class="{
              'v2-dropdown__option--active': isSelected(option),
              'v2-dropdown__option--success': option && option.variant === 'success',
              'v2-dropdown__option--destructive': option && option.variant === 'destructive',
            }"
            @click="onSelect(option)"
          >
            {{ optionLabel(option) }}
          </button>
          <button
            v-if="canCreate"
            class="v2-dropdown__option v2-dropdown__option--create"
            @click="onCreateOption"
          >
            Use "{{ searchText }}"
          </button>
        </div>
      </slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'V2Dropdown',
  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = e => {
          if (!el.contains(e.target)) {
            binding.value()
          }
        }
        document.addEventListener('click', el._clickOutside)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside)
      },
    },
  },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    value: {
      type: [String, Number, Object],
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Select',
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    labelKey: {
      type: String,
      default: '',
    },
    valueKey: {
      type: String,
      default: '',
    },
    emptyText: {
      type: String,
      default: 'Nothing was found.',
    },
    align: {
      type: String,
      default: 'left',
      validator: v => ['left', 'right'].includes(v),
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      searchText: '',
    }
  },
  computed: {
    displayLabel() {
      if (this.value === null || this.value === undefined || this.value === '') return this.placeholder
      if (typeof this.value === 'object') {
        const key = this.labelKey || 'label'
        return key in this.value ? this.value[key] : String(this.value)
      }
      // value is a primitive — find the matching option to show its label
      const match = this.options.find(opt => this.optionValue(opt) === this.value)
      return match ? this.optionLabel(match) : String(this.value)
    },
    filteredOptions() {
      if (!this.searchable || !this.searchText) return this.options
      const q = this.searchText.toLowerCase()
      return this.options.filter(opt => {
        const text = this.optionLabel(opt)
        return String(text).toLowerCase().includes(q)
      })
    },
    canCreate() {
      if (!this.allowCreate || !this.searchText.trim()) return false
      // Only show "Use X" when the typed text isn't already an exact match
      const exact = this.options.some(
        opt => String(this.optionLabel(opt)).toLowerCase() === this.searchText.trim().toLowerCase(),
      )
      return !exact
    },
  },
  watch: {
    isOpen(val) {
      if (val && this.searchable) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus()
        })
      }
      if (!val) {
        this.searchText = ''
      }
    },
  },
  methods: {
    optionLabel(option) {
      if (typeof option === 'object' && option !== null) {
        const key = this.labelKey || 'label'
        return key in option ? option[key] : String(option)
      }
      return String(option)
    },
    optionValue(option) {
      if (typeof option === 'object' && option !== null) {
        const key = this.valueKey || 'value'
        return key in option ? option[key] : option
      }
      return option
    },
    isSelected(option) {
      return this.optionValue(option) === this.value
    },
    onSelect(option) {
      this.$emit('select', this.optionValue(option))
      this.isOpen = false
      this.searchText = ''
    },
    onCreateOption() {
      const val = this.searchText.trim()
      if (val) {
        this.$emit('select', val)
        this.isOpen = false
        this.searchText = ''
      }
    },
    onEnterCreate() {
      if (this.canCreate) {
        this.onCreateOption()
      } else if (this.filteredOptions.length === 1) {
        this.onSelect(this.filteredOptions[0])
      }
    },
    closeDropdown() {
      this.isOpen = false
      this.searchText = ''
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-dropdown {
  position: relative;

  // Clean opaque-white toggle — replaced the recessed-pocket glass recipe.
  &__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    // Trigger — no `backdrop-filter` (filter bars often have many of these
    // side-by-side). Menu below keeps its blur (only one open at a time).
    background: var(--v2-glass-surface-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
    min-height: 38px;

    &:hover {
      border-color: var(--v2-glass-stroke-strong);
    }

    &--has-value {
      color: var(--v2-foreground);
    }
  }

  &__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chevron {
    flex-shrink: 0;
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  // Floating surface — near-opaque white so dark text stays readable when
  // the menu opens over arbitrary content (product images, dark cards).
  &__menu {
    position: absolute;
    top: calc(100% + var(--v2-space-1));
    left: 0;
    width: 100%;
    min-width: 160px;
    background: var(--v2-glass-surface-floating);
    backdrop-filter: blur(var(--v2-glass-blur-sm)) saturate(160%);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: 1rem;
    box-shadow: var(--v2-glass-shadow-floating);
    z-index: 100;
    max-height: 300px;
    overflow-y: auto;

    &--right {
      left: auto;
      right: 0;
    }
  }

  &__search {
    padding: var(--v2-space-2) var(--v2-space-3);
    border-bottom: 1px solid var(--v2-border);
  }

  &__search-input {
    width: 100%;
    padding: var(--v2-space-1) var(--v2-space-2);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    background: var(--v2-background);
    border: 1px solid var(--v2-input);
    border-radius: var(--v2-radius-sm);
    outline: none;

    &:focus {
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 2px rgba(116, 125, 238, 0.15);
    }

    &::placeholder {
      color: var(--v2-muted-foreground);
    }
  }

  &__empty {
    padding: var(--v2-space-4);
    text-align: center;
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
  }

  &__options {
    padding: var(--v2-space-1) 0;
  }

  &__option {
    display: block;
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    background: transparent;
    border: none;
    text-align: left;
    cursor: pointer;
    transition: background 0.1s ease;

    &:hover {
      background: var(--v2-muted);
    }

    &--active {
      color: var(--v2-primary);
      font-weight: 500;
      background: var(--v2-muted);
    }

    &--success {
      color: var(--v2-success);
    }

    &--destructive {
      color: var(--v2-destructive);
    }

    &--create {
      color: var(--v2-primary);
      font-style: italic;
      border-top: 1px solid var(--v2-border);
      margin-top: var(--v2-space-1);
    }
  }
}
</style>
